import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type EventListItem } from '../lib/api';
import { MON, dLong, dShort, hm, plural, rel, timeLabel, ymd } from '../lib/domain';
import { eventTooltip } from '../lib/md';
import {
  dayMap,
  gridRange,
  monthGrid,
  monthKey,
  parseDay,
  parseMonth,
  shiftMonth,
  startsOn,
  type Month,
} from '../lib/calendar';
import { dc } from '../lib/style';
import { DiscChips, ErrorBox, Icon, Loading, PageHead } from '../ui';
import { Card } from './Termine';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const going = (e: EventListItem) => e.my_status === 'yes' || e.my_status === 'wait';
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const ARROWS: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/** Month view of all events. Month and selected day live in the URL (?monat=2026-11&tag=2026-11-05). */
export default function Kalender() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const panel = useRef<HTMLElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const focusDay = useRef<string | null>(null); // after arrow keys: move focus to the new day once it is rendered
  const lastPick = useRef<Date | null>(null); // fast key presses build on the newest day, even before re-render
  const now = new Date();
  const month = parseMonth(params.get('monat'), now);
  const selected =
    parseDay(params.get('tag')) ??
    (monthKey(month) === monthKey(parseMonth(null, now)) ? now : new Date(month.y, month.m, 1));
  const [mine, setMine] = useState(false);
  const [discs, setDiscs] = useState<Set<string>>(new Set());

  const { from, to } = gridRange(month);
  const { data, isLoading, error } = useQuery({
    queryKey: ['events', 'range', from.toISOString(), to.toISOString()],
    queryFn: () =>
      api<EventListItem[]>(
        `/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      ),
    placeholderData: prev => prev, // keep the old month visible while the next one loads
  });
  const map = useMemo(
    // "Nur meine" means the same as in the list: every event I answered (yes, waitlist, maybe)
    () =>
      dayMap((data ?? []).filter(e => (!mine || e.my_status !== 'none') && (!discs.size || discs.has(e.discipline)))),
    [data, mine, discs],
  );

  const go = (m: Month, day?: Date) =>
    setParams({ monat: monthKey(m), ...(day ? { tag: ymd(day) } : {}) }, { replace: true });
  const pick = (d: Date, scroll = true) => {
    go({ y: d.getFullYear(), m: d.getMonth() }, d);
    // on phones the day list is below the grid
    if (scroll && window.innerWidth < 1120)
      requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: 'smooth' }));
  };
  useEffect(() => {
    if (!focusDay.current) return;
    const cell = grid.current?.querySelector<HTMLElement>(`[data-day="${focusDay.current}"]`);
    if (!cell) return; // that month is not rendered yet – the next render tries again
    cell.focus();
    focusDay.current = null;
  }, [params]);
  const today = ymd(now);
  const days = monthGrid(month);
  const weeks = Array.from({ length: days.length / 7 }, (_, i) => days.slice(i * 7, i * 7 + 7));
  const sel = ymd(selected);
  const dayEvents = map.get(sel) ?? [];
  const selStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
  const isFuture = selStart.getTime() >= new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  const toToday = () => go(parseMonth(null, now), now);
  return (
    <>
      <PageHead
        className="ph-cal"
        bar="Kalender"
        keep
        title={
          <>
            {MON[month.m]} <span className="light">{month.y}</span>
          </>
        }
        right={
          <div className="cal-nav">
            <button className="icon-btn" aria-label="Vorheriger Monat" onClick={() => go(shiftMonth(month, -1))}>
              <Icon name="left" sw={2} />
            </button>
            <button className="btn btn-ghost cal-today" onClick={toToday}>
              Heute
            </button>
            <button className="icon-btn" aria-label="Nächster Monat" onClick={() => go(shiftMonth(month, 1))}>
              <Icon name="right" sw={2} />
            </button>
          </div>
        }
      />
      <div className="toolbar">
        {/* phones: "Heute" sits here, so the month title and the arrows always fit in one row */}
        <button className="btn btn-ghost cal-today-bar" onClick={toToday}>
          Heute
        </button>
        <button className="toggle" aria-pressed={mine} onClick={() => setMine(!mine)}>
          <Icon name="user" sw={1.8} />
          Nur meine
        </button>
        <DiscChips value={discs} onChange={setDiscs} />
      </div>
      {error ? (
        <ErrorBox error={error} />
      ) : isLoading && !data ? (
        <Loading />
      ) : (
        <div className="layout cal-layout">
          <div style={{ minWidth: 0 }}>
            <div
              className="cal"
              role="grid"
              ref={grid}
              aria-label={`Monatsansicht ${MON[month.m]} ${month.y}`}
              aria-describedby="cal-help"
            >
              <div role="row" style={{ display: 'contents' }}>
                {WEEKDAYS.map(d => (
                  <div key={d} className="cal-wd" role="columnheader">
                    {d}
                  </div>
                ))}
              </div>
              {weeks.map(week => (
                <div key={ymd(week[0])} role="row" style={{ display: 'contents' }}>
                  {week.map(d => {
                    const k = ymd(d);
                    const evs = map.get(k) ?? [];
                    const cls = ['cal-day'];
                    if (d.getMonth() !== month.m) cls.push('out');
                    if (k === today) cls.push('today');
                    if (k === sel) cls.push('sel');
                    return (
                      <div
                        key={k}
                        className={cls.join(' ')}
                        role="gridcell"
                        // roving focus: only the selected day is a tab stop, arrow keys move between days
                        tabIndex={k === sel ? 0 : -1}
                        aria-selected={k === sel}
                        aria-label={`${dLong(d)}, ${plural(evs.length, 'Termin', 'Termine')}`}
                        data-day={k}
                        onClick={() => pick(d)}
                        onKeyDown={ev => {
                          if (ev.target !== ev.currentTarget) return; // keys on the pills inside are theirs
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            pick(d);
                          } else if (ev.key in ARROWS) {
                            ev.preventDefault();
                            const base = lastPick.current && focusDay.current ? lastPick.current : d;
                            const next = addDays(base, ARROWS[ev.key]);
                            lastPick.current = next;
                            pick(next, false);
                            focusDay.current = ymd(next);
                          }
                        }}
                      >
                        <span className="num">{d.getDate()}</span>
                        {evs.slice(0, 3).map(e => (
                          <button
                            key={e.id}
                            className={`pill${going(e) ? ' mine' : ''}${e.cancelled ? ' cancel' : ''}`}
                            style={dc(e.discipline)}
                            title={eventTooltip(e, `${dShort(new Date(e.starts_at))} · ${timeLabel(e)}`)}
                            tabIndex={-1} // keyboard: the day list next to the grid has the same events
                            onClick={ev => {
                              ev.stopPropagation();
                              void nav(`/termine/${e.id}`);
                            }}
                          >
                            {startsOn(e, d) ? <span className="tnum">{hm(new Date(e.starts_at))}</span> : '↳'} {e.title}
                          </button>
                        ))}
                        {evs.length > 3 && <span className="cal-more">+{evs.length - 3} weitere</span>}
                        <span className="cal-dots" aria-hidden="true">
                          {evs.slice(0, 4).map(e => (
                            <i key={e.id} style={dc(e.discipline)} />
                          ))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <p id="cal-help" className="sr-only">
              Mit den Pfeiltasten zwischen den Tagen wechseln. Die Termine des gewählten Tages stehen in der Liste
              daneben.
            </p>
          </div>
          <aside className="rail" ref={panel}>
            <div className="panel daypanel">
              <p className="eyebrow">{sel === today ? 'Heute' : rel(selected)}</p>
              <h3>{dLong(selected)}</h3>
              {dayEvents.length ? (
                <div className="day-events">
                  {dayEvents.map(e => (
                    <Card key={e.id} e={e} />
                  ))}
                </div>
              ) : (
                <p>Keine Termine an diesem Tag.</p>
              )}
              {isFuture && (
                <div style={{ marginTop: 14 }}>
                  <Link className="btn btn-ghost btn-sm" to={`/termine/neu?datum=${sel}`}>
                    <Icon name="plus" sw={2} />
                    Termin an diesem Tag
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
